import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiUser, FiMail, FiPhone, FiEdit, FiTrash2, FiBriefcase, FiSearch, FiGrid, FiList, FiX, FiMapPin, FiGitMerge, FiAlertTriangle, FiLink } from 'react-icons/fi';
import { getClients, deleteClient, createClient, mergeClient, getMergePreview } from '../services/api';

function Clients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('clientsViewMode') || 'grid';
  });
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSourceClient, setMergeSourceClient] = useState(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    notes: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    // Filter clients based on search term
    if (!searchTerm.trim()) {
      setFilteredClients(clients);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredClients(clients.filter(client => 
        client.name?.toLowerCase().includes(term) ||
        client.company_name?.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.toLowerCase().includes(term) ||
        client.address?.toLowerCase().includes(term) ||
        client.city?.toLowerCase().includes(term)
      ));
    }
  }, [clients, searchTerm]);

  useEffect(() => {
    localStorage.setItem('clientsViewMode', viewMode);
  }, [viewMode]);

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data.clients);
      setLoading(false);
    } catch (error) {
      console.error('Error loading clients:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await deleteClient(clientId);
        loadClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        alert(error.response?.data?.error || 'Failed to delete client');
      }
    }
  };

  const openMergeModal = (client) => {
    setMergeSourceClient(client);
    setMergeTargetId('');
    setMergePreview(null);
    setShowMergeModal(true);
  };

  const loadMergePreview = async (targetId) => {
    if (!targetId || !mergeSourceClient) {
      setMergePreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const response = await getMergePreview(mergeSourceClient.id, parseInt(targetId));
      setMergePreview(response.data);
    } catch (error) {
      console.error('Error loading merge preview:', error);
      setMergePreview(null);
    }
    setLoadingPreview(false);
  };

  const handleTargetChange = (e) => {
    const targetId = e.target.value;
    setMergeTargetId(targetId);
    loadMergePreview(targetId);
  };

  const handleMerge = async () => {
    if (!mergeTargetId) {
      alert('Please select a client to merge into');
      return;
    }
    const warningMsg = mergePreview?.has_warnings
      ? `\n\nWARNING: Potential duplicate jobs detected! Review the list carefully before proceeding.`
      : '';
    if (window.confirm(`Are you sure you want to merge "${mergeSourceClient.name}" into the selected client? This will move all jobs, invoices, and quotes, then delete "${mergeSourceClient.name}".${warningMsg}`)) {
      try {
        const response = await mergeClient(mergeSourceClient.id, parseInt(mergeTargetId));
        alert(response.data.message);
        setShowMergeModal(false);
        setMergeSourceClient(null);
        setMergePreview(null);
        loadClients();
      } catch (error) {
        console.error('Error merging client:', error);
        alert(error.response?.data?.error || 'Failed to merge client');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createClient(formData);
      setFormData({ 
        name: '', 
        company_name: '',
        email: '', 
        phone: '', 
        address: '', 
        city: '',
        state: '',
        zip_code: '',
        notes: '' 
      });
      setShowForm(false);
      loadClients();
    } catch (error) {
      console.error('Error creating client:', error);
      alert('Failed to create client');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Format full address for display
  const formatAddress = (client) => {
    const parts = [];
    if (client.address) parts.push(client.address);
    const cityStateZip = [
      client.city,
      client.state,
      client.zip_code
    ].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join(', ');
  };

  if (loading) {
    return <div className="loading">Loading clients...</div>;
  }

  return (
    <div className="clients-page">
      <div className="page-header">
        <div>
          <h1>Clients</h1>
          <p className="subtitle">Manage your client database</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="btn"
            onClick={() => {
              const phoneRaw = window.prompt("Client's mobile number:");
              if (!phoneRaw) return;
              const phone = phoneRaw.replace(/[^0-9+]/g, '');
              if (!phone) { alert('No phone number entered.'); return; }
              const link = `${window.location.origin}/new-client`;
              const body = encodeURIComponent(
                `Hi! Please take a minute to fill out your contact info so we can get you in our system: ${link}\n\nThanks!\nNorth Georgia Surveying`
              );
              // sms:PHONE?body=BODY works on both iOS and Android in modern versions
              window.location.href = `sms:${phone}?body=${body}`;
            }}
            style={{ backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}
            title="Open your phone's text app with the intake link pre-filled"
          >
            <FiPhone /> Text Intake Link
          </button>
          <button
            className="btn"
            onClick={() => {
              const link = `${window.location.origin}/new-client`;
              navigator.clipboard.writeText(link).then(
                () => alert(`Client intake link copied:\n\n${link}`),
                () => window.prompt('Copy this client intake link:', link)
              );
            }}
            style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
            title="Copy the intake link to paste anywhere (email, etc.)"
          >
            <FiLink /> Copy Intake Link
          </button>
          <button
            className="btn"
            onClick={() => {
              const phoneRaw = window.prompt("Client's mobile number:");
              if (!phoneRaw) return;
              const phone = phoneRaw.replace(/[^0-9+]/g, '');
              if (!phone) { alert('No phone number entered.'); return; }
              const link = `${window.location.origin}/get-a-quote`;
              const body = encodeURIComponent(
                `Hi! Please fill out a quick form so we can put together a quote for you: ${link}\n\nThanks!\nNorth Georgia Surveying`
              );
              window.location.href = `sms:${phone}?body=${body}`;
            }}
            style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
            title="Open your phone's text app with the quote request link pre-filled"
          >
            <FiPhone /> Text Quote Link
          </button>
          <button
            className="btn"
            onClick={() => {
              const link = `${window.location.origin}/get-a-quote`;
              navigator.clipboard.writeText(link).then(
                () => alert(`Quote request link copied:\n\n${link}`),
                () => window.prompt('Copy this quote request link:', link)
              );
            }}
            style={{ backgroundColor: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' }}
            title="Copy the quote request link to paste anywhere"
          >
            <FiLink /> Copy Quote Link
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(!showForm)}
          >
            <FiPlus /> {showForm ? 'Cancel' : 'Add Client'}
          </button>
        </div>
      </div>

      {/* Search and View Toggle */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <FiSearch style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#9ca3af'
          }} />
          <input
            type="text"
            placeholder="Search by name, company, email, phone, or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 40px 12px 44px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '15px'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                padding: '4px'
              }}
            >
              <FiX size={18} />
            </button>
          )}
        </div>
        
        {searchTerm && (
          <span style={{ 
            color: '#666', 
            fontSize: '14px',
            backgroundColor: '#f3f4f6',
            padding: '8px 12px',
            borderRadius: '6px'
          }}>
            {filteredClients.length} result{filteredClients.length !== 1 ? 's' : ''}
          </span>
        )}
        
        <div style={{
          display: 'flex',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{
              padding: '10px 14px',
              border: 'none',
              backgroundColor: viewMode === 'grid' ? '#2563eb' : 'white',
              color: viewMode === 'grid' ? 'white' : '#666',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FiGrid size={18} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{
              padding: '10px 14px',
              border: 'none',
              borderLeft: '1px solid #e5e7eb',
              backgroundColor: viewMode === 'list' ? '#2563eb' : 'white',
              color: viewMode === 'list' ? 'white' : '#666',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FiList size={18} />
          </button>
        </div>
      </div>

      {/* Quick Add Form */}
      {showForm && (
        <div className="client-form-card">
          <h2>Add New Client</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="ABC Construction LLC"
                />
              </div>
              <div className="form-group">
                <label>Contact Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-control"
                  placeholder="John Smith"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="john@example.com"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Street Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-control"
                placeholder="123 Main St"
              />
            </div>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Atlanta"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>State</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="GA"
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>ZIP</label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="30103"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                className="form-control"
                placeholder="Additional notes..."
              />
            </div>
            <button type="submit" className="btn btn-primary">Create Client</button>
          </form>
        </div>
      )}

      {/* Clients List */}
      {filteredClients.length > 0 ? (
        viewMode === 'grid' ? (
          // Grid View
          <div className="clients-grid">
            {filteredClients.map(client => (
              <div key={client.id} className="client-card">
                <div className="client-header">
                  <div className="client-avatar">
                    {client.company_name ? <FiBriefcase /> : <FiUser />}
                  </div>
                  <div className="client-actions">
                    <Link to={`/clients/${client.id}`} className="btn-icon">
                      <FiEdit />
                    </Link>
                    <button
                      onClick={() => openMergeModal(client)}
                      className="btn-icon"
                      title="Merge into another client"
                    >
                      <FiGitMerge />
                    </button>
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="btn-icon btn-danger"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
                
                <Link to={`/clients/${client.id}`} className="client-link">
                  {client.company_name ? (
                    <>
                      <h3>{client.company_name}</h3>
                      <p style={{ color: '#666', fontSize: '14px', margin: '4px 0 0' }}>{client.name}</p>
                    </>
                  ) : (
                    <h3>{client.name}</h3>
                  )}
                </Link>
                
                <div className="client-info">
                  {client.email && (
                    <div className="info-row">
                      <FiMail />
                      <span>{client.email}</span>
                    </div>
                  )}
                  {client.phone && (
                    <div className="info-row">
                      <FiPhone />
                      <span>{client.phone}</span>
                    </div>
                  )}
                </div>
                
                {formatAddress(client) && (
                  <p className="client-address">{formatAddress(client)}</p>
                )}
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Name</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Company</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Email</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Phone</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Location</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map(client => (
                  <tr 
                    key={client.id} 
                    style={{ borderBottom: '1px solid #e5e7eb' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <Link to={`/clients/${client.id}`} style={{ 
                        color: '#2563eb', 
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}>
                        {client.name}
                      </Link>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#666' }}>
                      {client.company_name || '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {client.email ? (
                        <a href={`mailto:${client.email}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                          {client.email}
                        </a>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {client.phone ? (
                        <a href={`tel:${client.phone}`} style={{ color: '#2563eb', textDecoration: 'none' }}>
                          {client.phone}
                        </a>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#666', fontSize: '14px' }}>
                      {client.city && client.state ? `${client.city}, ${client.state}` : (client.city || client.state || '-')}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <Link
                          to={`/clients/${client.id}`}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#e5e7eb',
                            borderRadius: '6px',
                            color: '#374151',
                            textDecoration: 'none',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <FiEdit size={14} /> Edit
                        </Link>
                        <button
                          onClick={() => openMergeModal(client)}
                          title="Merge into another client"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#dbeafe',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#2563eb',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <FiGitMerge size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#fee2e2',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : clients.length > 0 ? (
        <div className="empty-state">
          <FiSearch size={64} />
          <h2>No Results</h2>
          <p>No clients match your search "{searchTerm}"</p>
          <button 
            className="btn btn-secondary"
            onClick={() => setSearchTerm('')}
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="empty-state">
          <FiUser size={64} />
          <h2>No Clients Yet</h2>
          <p>Get started by adding your first client</p>
          <button
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <FiPlus /> Add Your First Client
          </button>
        </div>
      )}

      {/* Merge Client Modal */}
      {showMergeModal && mergeSourceClient && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '550px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Merge Client</h2>
              <button
                onClick={() => { setShowMergeModal(false); setMergePreview(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
              >
                <FiX size={20} />
              </button>
            </div>

            <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
              Merge <strong>"{mergeSourceClient.name}"</strong> into another client.
              All jobs, invoices, and quotes will be transferred to the target client,
              then this client will be deleted.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                Merge into:
              </label>
              <select
                value={mergeTargetId}
                onChange={handleTargetChange}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px'
                }}
              >
                <option value="">Select a client...</option>
                {clients
                  .filter(c => c.id !== mergeSourceClient.id)
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.company_name ? `${c.company_name} (${c.name})` : c.name}
                    </option>
                  ))
                }
              </select>
            </div>

            {/* Preview Section */}
            {loadingPreview && (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Loading preview...
              </div>
            )}

            {mergePreview && !loadingPreview && (
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>What will be transferred:</h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                  <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#2563eb' }}>{mergePreview.source_jobs?.length || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Jobs</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#2563eb' }}>{mergePreview.source_invoices_count || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Invoices</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#2563eb' }}>{mergePreview.source_quotes_count || 0}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>Quotes</div>
                  </div>
                </div>

                {/* Jobs list */}
                {mergePreview.source_jobs?.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '8px' }}>Jobs to transfer:</div>
                    <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px' }}>
                      {mergePreview.source_jobs.map(job => (
                        <div key={job.id} style={{ padding: '6px 8px', backgroundColor: 'white', marginBottom: '4px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between' }}>
                          <span><strong>{job.job_number}</strong> - {job.address || 'No address'}</span>
                          <span style={{ color: '#666' }}>{job.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {mergePreview.has_warnings && (
                  <div style={{ padding: '12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#b45309', fontWeight: '600', marginBottom: '8px' }}>
                      <FiAlertTriangle /> Potential Duplicates Detected
                    </div>
                    {mergePreview.duplicate_job_numbers?.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '6px' }}>
                        <strong>Same job numbers:</strong> {mergePreview.duplicate_job_numbers.join(', ')}
                      </div>
                    )}
                    {mergePreview.similar_address_jobs?.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#92400e' }}>
                        <strong>Same addresses:</strong>
                        {mergePreview.similar_address_jobs.map((pair, i) => (
                          <div key={i} style={{ marginLeft: '10px', marginTop: '4px' }}>
                            • {pair.source_job.job_number} & {pair.target_job.job_number}: {pair.source_job.address}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowMergeModal(false); setMergePreview(null); }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleMerge}
                disabled={!mergeTargetId || loadingPreview}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: mergeTargetId && !loadingPreview ? (mergePreview?.has_warnings ? '#f59e0b' : '#2563eb') : '#93c5fd',
                  color: 'white',
                  cursor: mergeTargetId && !loadingPreview ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <FiGitMerge size={16} /> Merge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients;